import { SectionItem, SmartPay } from '@features/shift-post-v2/components'
import {
  Box,
  Flex,
  Text,
  InputV2,
  SwitchV2,
  Alert,
} from '@pinchenterprisesnpm/friday-ui'
import { connect } from 'formik'
import { SHIFT_PROGRESS, SHIFT_TYPE } from '@network/models/shift.model'
import { MultiSingleDayPicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/multi-single-day-picker/multi-single-day-picker'
import { IndividualDaysList } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/individual-days-list/indivitual-days-list'
import {
  getBreakValue,
  IN_PROGRESS_MULTIDAY_SHIFT_ALERT_MESSAGE,
  IN_PROGRESS_MULTIDAY_SHIFT_NO_EDIT_ALERT_MESSAGE,
  shouldShowOTPolicyWarningV2,
} from '@src/helpers/shiftHelpers'
import { faAlarmClock } from '@fortawesome/pro-solid-svg-icons'
import { faDollarSign } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GenericTooltip } from '@src/components/common/GenericTooltip'
import { PAY_RATE_UPDATE_RESTRICTION_TOOLTIP } from '@src/config/TooltipContent/TooltipContent'
import { hasBusinessPayRateUpdateRestriction } from '@src/helpers/businessEntityHelpers'
import { OTPolicyWarning } from '@src/components/business/OTPolicyWarning'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { SingleDayPicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/single-day-picker/single-day-picker'
import { ShiftPostStep } from '@src/features/shift-post-v2/steps/steps.types'
import { useModal } from '@src/hooks/useModal'
import { EditScheduleModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/modals/edit-schedule-modal/edit-schedule-modal'
import { ScheduleSectionContainer } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.style'
import { EditShiftModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/modals/edit-shift-modal/edit-shift-modal'
import { RecurringDayPicker } from '@features/shift-post-v2/components'
import { PayRateFeedback } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/components/pay-rate-feedback'
import { FEATURE_TOGGLE_MULTIDAY_REVAMP } from '@src/config/featureToggles'
import { checkIfBusinessRequiresNextWorkingDaySelection } from '@src/helpers/shiftHelpers'
import { useMemo } from 'react'
import { ProTipSegment } from '@src/features/shift-post-v2/components/smart-pay/components'
import AuthService from '@src/helpers/authService'
import { IStrategySelectionShiftParams } from '@src/features/shift-post-v2/components/smart-pay/smart-pay.types'
import { CustomTimePicker } from './components/custom-time-picker'
import CustomDropdownV5 from './components/custom-dropdown-v5/custom-dropdown-v5'
import {
  handlePayRateChange,
  handleHourChange,
  handleBreakChange,
} from './form-handlers'
import { IScheduleSectionProps } from './schedule-section.types'

export const ScheduleSection = connect<IScheduleSectionProps>(
  ({ formik, sourcePage, sourceStep }) => {
    const businessEntity = AuthService.getBusinessEntity()
    const isSmartPayEnabled =
      businessEntity?.business_setting?.suggested_pay_rate_strategy_enabled ??
      false
    const [isEditModalOpen, toggleEditModal, closeEditModal] = useModal()
    const [
      isEditShiftModalOpen,
      toggleEditShiftModal,
      closeEditShiftModal,
    ] = useModal()

    const { values, setFieldValue, handleBlur, errors } = formik
    const { location } = values
    const isConfirmStep = sourceStep === ShiftPostStep.CONFIRM

    const strategySelectionParams: IStrategySelectionShiftParams = {
      shiftType: values.type,
      singleDayDate:
        values.single_day_shifts?.[0]?.date_start ?? values.single_day_date,
      hourStart: values.hour_start,
      days: values.days,
      propertyTimezone: values.location?.timezone,
    }

    const isEditInProgressMultidayShift =
      Number(values?.type) === SHIFT_TYPE.MULTIDAY &&
      Number(values?.progress) === SHIFT_PROGRESS.ACTIVE &&
      FEATURE_TOGGLE_MULTIDAY_REVAMP &&
      sourcePage === ShiftPostPage.EDIT_SHIFT

    const payRateLimit = location?.active_overtime_policy?.pay_rate_limit

    const showOTPolicyWarning = payRateLimit
      ? shouldShowOTPolicyWarningV2(
          values,
          payRateLimit,
          sourcePage === ShiftPostPage.EDIT_SHIFT,
        )
      : false

    /**
     * Field editing is disabled if template is not selected.
     */
    const editingDisabled =
      (sourcePage === ShiftPostPage.POST_SHIFT && !values.template_id) ||
      isEditInProgressMultidayShift

    const isEditShiftPage = sourcePage === ShiftPostPage.EDIT_SHIFT
    const onlyOneDay = isEditShiftPage
      ? !!values.single_day_date
      : values.single_day_shifts?.length === 1
    const isSingleDay = values.type === SHIFT_TYPE.SINGLEDAY

    const shouldShowEditShiftModal = onlyOneDay && isSingleDay

    const shouldDisplaySmartPay = useMemo(() => {
      return (
        isSmartPayEnabled &&
        values.suggested_pay_rates &&
        !hasBusinessPayRateUpdateRestriction() &&
        !editingDisabled
      )
    }, [editingDisabled, isSmartPayEnabled, values.suggested_pay_rates])

    // Check if dates are selected for Confirm step validation
    const hasDateSelected = useMemo(() => {
      if (!isConfirmStep) return true

      if (isSingleDay) {
        if (isEditShiftPage) {
          return !!values.single_day_date
        } else {
          return values.single_day_shifts && values.single_day_shifts.length > 0
        }
      } else {
        // MultiDay shift
        return !!(values.multiday_day_start && values.multiday_day_end)
      }
    }, [
      isConfirmStep,
      isSingleDay,
      isEditShiftPage,
      values.single_day_date,
      values.single_day_shifts,
      values.multiday_day_start,
      values.multiday_day_end,
    ])

    const activeMultidayBanner = useMemo(() => {
      if (isEditInProgressMultidayShift) {
        const message = checkIfBusinessRequiresNextWorkingDaySelection()
          ? IN_PROGRESS_MULTIDAY_SHIFT_ALERT_MESSAGE
          : IN_PROGRESS_MULTIDAY_SHIFT_NO_EDIT_ALERT_MESSAGE

        return <Alert message={message} type="warning" />
      }
      return null
    }, [isEditInProgressMultidayShift])

    if (isConfirmStep) {
      return (
        <SectionItem
          title="Schedule"
          hasEdit
          isEditDisabled={editingDisabled}
          editDisabledTooltip="Please select a template first to edit the schedule."
          onEdit={
            shouldShowEditShiftModal ? toggleEditShiftModal : toggleEditModal
          }
        >
          <ScheduleSectionContainer>
            {!hasDateSelected && (
              <>
                <Text fontSize="medium" mt="xs">
                  No date selected.
                </Text>
                <Text color="danger" my="0" mb="md" fontSize="small">
                  Please select a date for your shift.
                </Text>
              </>
            )}
            <IndividualDaysList
              sourcePage={sourcePage}
              sourceStep={sourceStep}
              isEditable={false}
            />
          </ScheduleSectionContainer>

          <EditScheduleModal
            isOpen={isEditModalOpen}
            closeModal={closeEditModal}
            sourcePage={sourcePage}
          />

          {shouldShowEditShiftModal && (
            <EditShiftModal
              customIsOpen={isEditShiftModalOpen}
              customCloseModal={closeEditShiftModal}
              hasCustomToggleButton
              day={isEditShiftPage ? undefined : values.single_day_shifts?.[0]}
              singleDayDate={
                isEditShiftPage ? values.single_day_date : undefined
              }
              directlyEditPayRate
            />
          )}
        </SectionItem>
      )
    }
    return (
      <SectionItem title="Schedule">
        {activeMultidayBanner}
        <Flex alignItems="center">
          <Text mr="sm" color="gray">
            Recurring Shift
          </Text>

          <SwitchV2
            value={values.is_recurring_shift}
            onChange={(value: boolean) => {
              setFieldValue('is_recurring_shift', value)
              setFieldValue(
                'type',
                value ? SHIFT_TYPE.MULTIDAY : SHIFT_TYPE.SINGLEDAY,
              )
            }}
            isDisabled={isEditInProgressMultidayShift}
          />
        </Flex>

        {/* Date picker */}
        <Flex mt="md">
          <Box mr="md">
            {values.type === SHIFT_TYPE.SINGLEDAY &&
              [ShiftPostPage.POST_SHIFT, ShiftPostPage.COPY_SHIFT].includes(
                sourcePage,
              ) && (
                <MultiSingleDayPicker
                  formik={formik}
                  isDisabled={editingDisabled}
                  customWidth="420px"
                />
              )}

            {values.type === SHIFT_TYPE.SINGLEDAY &&
              sourcePage === ShiftPostPage.EDIT_SHIFT && (
                <SingleDayPicker
                  formik={formik}
                  isDisabled={editingDisabled}
                  customWidth="420px"
                />
              )}

            {values.type === SHIFT_TYPE.MULTIDAY && (
              <RecurringDayPicker
                formik={formik}
                isDisabled={editingDisabled}
                sourcePage={sourcePage}
              />
            )}
          </Box>
        </Flex>
        {/* Time picker */}
        <Flex mt="md">
          <Box mr="md">
            <CustomTimePicker
              value={values.hour_start}
              onChange={value => handleHourChange(value, true, formik)}
              label="Start Time"
              hasError={!!errors.hour_start}
              width="200px"
              isDisabled={editingDisabled}
            />
          </Box>

          <Box>
            <CustomTimePicker
              value={values.hour_end}
              label="End Time"
              width="200px"
              hasError={!!errors.hour_end}
              onChange={value => handleHourChange(value, false, formik)}
              isDisabled={editingDisabled}
            />
          </Box>
        </Flex>
        {/* Pay rate and unpaid break */}
        <Flex mt="md">
          {!shouldDisplaySmartPay && (
            <Flex mr="md" alignItems="center">
              <InputV2
                leftElement={
                  <FontAwesomeIcon
                    icon={faDollarSign}
                    size="lg"
                    color="#C5C5C7"
                    style={{
                      height: 24,
                      width: 24,
                    }}
                  />
                }
                labelStyles={{
                  color: '#868686',
                  fontWeight: 400,
                }}
                leftElementStyles={{
                  top: 8,
                  left: -1,
                }}
                numberArrowIconColor="gray.400"
                numberArrowsWrapperStyles={{
                  right: 3,
                }}
                label="Hourly Pay Rate"
                placeholder=""
                type="number"
                name="pay_value"
                id="pay_value"
                width={
                  hasBusinessPayRateUpdateRestriction() ? '180px' : '200px'
                }
                value={values.pay_value}
                isDisabled={
                  hasBusinessPayRateUpdateRestriction() || editingDisabled
                }
                onChange={e => handlePayRateChange(e, formik)}
                onBlur={handleBlur}
                errorMessage={errors.pay_value}
                onKeyDown={e => {
                  if (['e', 'E', '-', '+'].includes(e.key)) {
                    e.preventDefault()
                  }
                }}
              />
              {hasBusinessPayRateUpdateRestriction() && (
                <Box mt="xs">
                  <GenericTooltip
                    message={PAY_RATE_UPDATE_RESTRICTION_TOOLTIP}
                    tooltipPlacement="bottom"
                  />
                </Box>
              )}
            </Flex>
          )}
          <Box height="50px">
            <CustomDropdownV5
              topLabel="Unpaid break"
              icon={faAlarmClock}
              options={getBreakValue()}
              value={
                getBreakValue().find(
                  opt => opt.value === (values.break || 0),
                ) || getBreakValue()[0]
              }
              onChange={(selected: any) => handleBreakChange(selected, formik)}
              isDisabled={editingDisabled}
            />
          </Box>
        </Flex>
        {!shouldDisplaySmartPay && (
          <PayRateFeedback
            suggestedPayRate={Number(values.suggested_pay_rate)}
            payValue={Number(values.pay_value)}
          />
        )}

        {shouldDisplaySmartPay && values.suggested_pay_rates && (
          <>
            <SmartPay
              payValue={values.pay_value}
              minPayRate={values.min_wage}
              handleBlur={handleBlur}
              onPayRateChange={e => handlePayRateChange(e, formik)}
              errorMessage={errors.pay_value}
              regularStrategy={values.suggested_pay_rates?.strategies.regular}
              lastMinuteStrategy={
                values.suggested_pay_rates?.strategies.last_minute
              }
              strategySelectionParams={strategySelectionParams}
            />

            <ProTipSegment
              regularStrategy={values.suggested_pay_rates?.strategies.regular}
              lastMinuteStrategy={
                values.suggested_pay_rates?.strategies.last_minute
              }
              strategySelectionParams={strategySelectionParams}
              mt="sm"
            />
          </>
        )}

        {showOTPolicyWarning && (
          <Box>
            <OTPolicyWarning payRate={payRateLimit ?? 0} />
          </Box>
        )}

        <IndividualDaysList sourcePage={sourcePage} sourceStep={sourceStep} />
      </SectionItem>
    )
  },
)
